<template>
  <div>
    <section class="dashboard grid-container">
      <div class="grid-x">
        <div class="cell medium-12">
          <h1>Competitions Overview</h1>
        </div>
        <div class="cell medium-4">
          <span class="compOverwiew__total"
            >Total: <strong>{{ overview.total }}</strong> competitions</span
          >
        </div>
        <div class="cell medium-8">
          <div class="grid-x grid-margin-x">
            <div class="cell medium-6">
              <multiselect
                v-model="search.countries"
                :options="countriesList"
                :close-on-select="true"
                :clear-on-select="false"
                label="name"
                class="pageTemplates__input compOverwiew__multiselect"
                track-by="id"
                select-label
                selected-label
                deselect-label
                :multiple="true"
                placeholder="All countries/regions"
                :allow-empty="true"
                @input="onSearchClick"
              />
            </div>
            <div class="cell medium-3">
              <flat-pickr
                v-model="search.from"
                :config="configDate"
                class="compOverwiew__input"
                placeholder="From"
                @on-change="onSearchClick"
              /><span class="compOverwiew__timezones">UTC</span>
            </div>
            <div class="cell medium-3">
              <flat-pickr
                v-model="search.to"
                :config="configDate"
                class="compOverwiew__input"
                placeholder="To"
                @on-change="onSearchClick"
              /><span class="compOverwiew__timezones">UTC</span>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section>
      <div class="grid-container">
        <h4 v-if="!showOverview">No competitions match the selected criteria</h4>
        <div class="grid-x">
          <div class="cell medium-12">
            <ul class="compOverwiew__chart">
              <li
                v-for="item in ordered"
                :key="item.status"
                :class="{
                  compOverwiew__chart__item: true,
                  ['compOverwiew__chart__item--' + item.status]: true,
                  compOverwiew__active: search.status === item.status,
                  'small-cell': item.percent < 15
                }"
                :style="{ width: item.percent + '%' }"
                :title="item.name"
                @click="setStatus(item.status)"
              >
                <h5>
                  {{ item.name }} <span> : {{ item.count }}</span>
                </h5>
                <p>{{ item.percent }}<span> %</span></p>
                <p />
                <div class="compOverwiew__chart__item__arrow" />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
    <section v-if="showOverview" class="dashboard grid-container">
      <div class="main-pages__box">
        <div class="grid-x">
          <div class="cell medium-6">
            <h3 class="compOverwiew__title compOverwiew__title--secondary compOverwiew__title--flat-top">
              Competitions {{ getInfo.title }}
            </h3>
            <p v-if="getInfo.info" class="compOverwiew__subline">{{ getInfo.info }}</p>
          </div>
        </div>
        <div class="compOverwiew__table">
          <v-server-table
            ref="cometitions_table"
            name="competitionsList"
            class="main-pages__users-table attributes__table"
            url
            :columns="columns"
            :options="options"
          >
            <div slot="edit" slot-scope="props" class="attributes__rowActionsWrapper">
              <i v-if="props.row.hasAccess" class="main-pages__visit" @click="goTo(props.row.competitionId)" />
            </div>
          </v-server-table>
        </div>
      </div>
    </section>

    <section v-if="showOverview" class="dashboard grid-container">
      <div class="main-pages__box">
        <div class="grid-x">
          <div class="cell medium-12">
            <h3 class="compOverwiew__title compOverwiew__title--secondary compOverwiew__title--flat-top">
              Export view data
            </h3>
            <p class="compOverwiew__subline">Export filtered data from this view as printable PDF or CSV file.</p>
          </div>
        </div>
        <div class="cell medium-12">
          Current filter:
          <strong>
            <span v-if="search.countries.length"> {{ searchCountries }}; </span>
            <span v-else> All countries/regions; </span>
            <span v-if="search.from">Dates from {{ formatDate(search.from, 'YYYY-MM-DD') }}</span>
            <span v-if="search.to"> to {{ formatDate(search.to, 'YYYY-MM-DD') }}</span>
          </strong>
        </div>
        <div class="cell medium-12">
          <div class="grid-x">
            <div class="cell medium-4 compOverwiew__export compOverwiew__export--pdf">
              <div class="compOverwiew__export__box" :disabled="downloading" @click="generatePdf">
                <div class="grid-x">
                  <div class="cell medium-9">
                    <h5>Download .PDF</h5>
                    <p>Printable file containing all<br />data on separate pages.</p>
                  </div>
                  <div class="cell medium-3">
                    <div class="compOverwiew__export__icon" />
                  </div>
                </div>
              </div>
            </div>
            <div class="cell medium-4 compOverwiew__export compOverwiew__export--csv">
              <div class="compOverwiew__export__box" :disabled="downloading" @click="generateCsv">
                <div class="grid-x">
                  <div class="cell medium-9">
                    <h5>Download .CSV</h5>
                    <p>Editable file with raw data.<br />Easy import to spreadsheet.</p>
                  </div>
                  <div class="cell medium-3">
                    <div class="compOverwiew__export__icon" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section class="dashboard grid-container">
      <h2 />
      <p />
    </section>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import moment from 'moment'
import { formatDate, formatDateWithTz, formatDateWithTzName } from '@/helpers/date.js'
import Multiselect from 'vue-multiselect'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import { orderBy } from 'lodash'
import { download } from '@/helpers/utils.js'

export default {
  components: {
    flatPickr,
    Multiselect
  },
  // eslint-disable-next-line vue/require-prop-types
  props: ['type'],
  data() {
    return {
      configDate: {
        dateFormat: 'Y-m-d',
        altInput: true,
        allowInput: true,
        altFormat: 'Y-m-d'
      },
      downloading: false,
      competitionsByType: {
        draft: {
          name: 'draft',
          title: 'Draft',
          info: ''
        },
        closed: {
          name: 'closed',
          title: 'Closed',
          info: ''
        },
        archived: {
          name: 'archived',
          title: 'Archived',
          info: ''
        },
        ready: {
          name: 'ready',
          title: 'Ready & Pending',
          info: 'All data are complited for competition. Start data is set up and waitng for lunch'
        },
        active: {
          name: 'active',
          title: 'Live',
          info: ''
        },
        completed: {
          name: 'completed',
          title: 'Completed',
          info: ''
        }
      },
      search: {
        countries: [],
        from: moment('2018-01-01').format('YYYY-MM-DD'),
        to: moment().startOf('day').add(12, 'month').format('YYYY-MM-DD'),
        status: 'draft'
      },
      total: 0,
      competitions: [],
      overview: []
    }
  },
  computed: {
    ...mapState('user', ['statuses', 'countriesList']),
    columns: function () {
      const columns = ['name', 'type', 'template', 'owner', 'total', 'countries', 'start', 'end', 'edit']
      if (['draft', 'ready'].includes(this.search.status)) {
        return columns.filter((i) => !['total'].includes(i))
      }
      return columns
    },
    showOverview: function () {
      return this.overview.total
    },
    getInfo: function () {
      return this.competitionsByType[this.search.status]
    },
    ordered: function () {
      return orderBy(this.overview.overview, 'order')
    },
    searchCountries: function () {
      return this.search.countries.map((i) => i.name).join(',')
    },
    options: function () {
      const self = this
      return {
        filterable: false,
        headings: {
          name: 'Name',
          type: 'Type',
          owner: 'Owner',
          total: 'Confirmed entries',
          countries: `Countries/regions`,
          start: this.search.status === 'draft' ? 'Created' : 'Competition start',
          end: this.search.status === 'draft' ? 'Last modified' : 'Competition end',
          edit: '  '
        },
        perPageValues: [5],
        orderBy: {
          column: 'name'
        },
        params: {
          ...this.search
        },
        requestFunction: this.getCompetitions,
        responseAdapter: function (resp) {
          const data = this.getResponseData(resp)
          return {
            data: data.docs.map((doc) => {
              const start = self.search.status === 'draft' ? doc.createdAt : doc.core.start_date
              const end = self.search.status === 'draft' ? doc.updatedAt : doc.core.end_date
              const timezone = `(${formatDateWithTzName(doc.core.timezone)})`
              return {
                hasAccess: data.accessIds.find((i) => i === doc._id),
                competitionId: doc._id,
                name: doc.core.name,
                type: doc.core.age.toUpperCase(),
                owner: doc.owner.email,
                total: doc.total,
                template: doc.template.id,
                countries: doc.core.countries.join(','),
                start: `${formatDateWithTz(start, doc.core.timezone, 'DD.MM.YYYY H:mm')} ${timezone}`,
                end: `${formatDateWithTz(end, doc.core.timezone, 'DD.MM.YYYY H:mm')} ${timezone}`
              }
            }),
            count: data.total
          }
        }
      }
    }
  },
  async created() {
    try {
      this.getCountriesList()
      this.setData()
    } catch (error) {
      throw error.response
    }
  },
  methods: {
    formatDate,
    goTo: function (competitionId) {
      this.redirectToPolling(competitionId)
    },
    ...mapActions('app', ['redirectToPolling']),
    ...mapActions('overview', ['getOverview', 'getPdf', 'getCsv', 'getCompetitions']),
    ...mapActions('user', ['getCountriesList']),
    setStatus: async function (status) {
      this.search.status = status
      this.$nextTick(async () => {
        this.refreshCompetitionTable()
        this.overview = await this.getOverview(this.search)
      })
    },
    onSearchClick: async function () {
      setTimeout(async () => {
        this.refreshCompetitionTable()
        this.setData()
      }, 0)
    },
    refreshCompetitionTable: function () {
      if (this.$refs.cometitions_table) {
        this.$refs.cometitions_table.refresh()
      }
    },
    generatePdf: async function () {
      try {
        this.downloading = true
        const response = await this.getPdf(this.search)
        download(response.data, 'overview.pdf')
        this.downloading = false
      } catch (error) {
        this.downloading = false
      }
    },
    generateCsv: async function () {
      try {
        this.downloading = true
        const response = await this.getCsv(this.search)
        download(response.data, 'overview.csv')
        this.downloading = false
      } catch (error) {
        this.downloading = false
        throw error
      }
    },
    async setData() {
      try {
        this.overview = await this.getOverview(this.search)
        const statuses = this.overview.overview.filter((i) => i.count)
        if (!statuses.includes(this.search.status) && statuses.length) {
          this.search.status = statuses.shift().status
          setTimeout(async () => {
            this.refreshCompetitionTable()
          })
        }
      } catch (error) {
        throw error.response
      }
    }
  }
}
</script>

<style src="./competitions-overview.scss" lang="scss"></style>



// WEBPACK FOOTER //
// src/components/pages/main-pages/competitions-overview/competitions-overview.vue